import { useForm, zodResolver } from '@mantine/form';
import { scrollToError } from '@shared/utils';
import { SelectItem } from '@components/v2/Select';
import { CITIZEN_STATUS, EMPLOYMENT_STATUS } from '@modules/opportunities/entities/opportunity/const';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { mapToTranslationKey } from '@modules/proposals/presentation/utils';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { USER_EVENTS } from '@shared/analytics/events';
import { createProposalSchema } from './validator';
import { PROPOSAL_CREATION_RULES } from '../../../../ProposalCreation/rules';

export const useCreateProposalState = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const form = useForm({
    initialValues: {
      first_name: '',
      last_name: '',
      residency_status: '',
      employment_status: '',
      offers_count: '',
    },
    validateInputOnChange: true,
    validate: zodResolver(createProposalSchema),
  });
  const offerOptions: SelectItem[] = Array.from({ length: PROPOSAL_CREATION_RULES.OFFER_AMOUNT_LIMIT }, (_, index) => ({
    value: `${index + 1}`,
    label: `${index + 1}`,
  }));
  const citizenStatusOptions: SelectItem[] = Object.entries(CITIZEN_STATUS).map(([_, value]) => ({
    value,
    label: t(mapToTranslationKey(value)),
  }));
  const employmentStatusOptions: SelectItem[] = Object.entries(EMPLOYMENT_STATUS)
    .filter(([_, value]) => value !== EMPLOYMENT_STATUS.soleProprietorship)
    .map(([_, value]) => ({
      value,
      label: t(mapToTranslationKey(value)),
    }));

  const handleOnSubmit = async () => {
    form.validate();
    if (!form.isValid()) {
      scrollToError(form.errors);
      return;
    }
    const data = form.values;
    const proposalId = crypto.randomUUID();
    trackAmplitudeEvent(USER_EVENTS.PROPOSAL.PROPOSAL_CREATION_ATTEMPTED, {
      useContext: true,
      addToContext: true,
    });
    navigate({
      to: `${proposalId}/template`,
      state: {
        id: proposalId,
        firstName: data.first_name,
        lastName: data.last_name,
        employmentStatus: data.employment_status,
        residencyStatus: data.residency_status,
        offersCount: Number(data.offers_count),
      } as any, // Remove this type bypass when route is defined in upcoming PR
    });
  };

  return {
    form,
    offerOptions,
    citizenStatusOptions,
    employmentStatusOptions,
    onSubmit: handleOnSubmit,
    resetForm: () => form.reset(),
  };
};
